import React, { type SyntheticEvent, useEffect, useState, type FC, type PropsWithChildren } from 'react';

import { throttle } from 'lodash';
import { isBrowser } from '@lucid/utils';
import { BREAKPOINT } from '@lucid/theme';

import Link from '../link/link';
import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Heading from '../heading/heading';
import reactHTMLParser from '../../utils/reactHTMLParser';
import Combobox from '../input/combobox/combobox';

const STICKY_NAV_HEIGHT = 64;

type BlogTOCProps = {
  /** The Drupal blog content paragraph entities */
  content: Lucid.Directus.Generated.Blog_Post_Translations_Content_Blocks[];
  /** The ID of the current section */
  currentSection?: string;
};

type DropdownTOCProps = {
  toc: TOCItem[];
  currentSection?: string | undefined;
};

export type BlogHTMLSection = {
  /** HTML value of blog section */
  html: string;
  /** ID of entire blog section */
  sectionID: string;
};

export type TOCItem = {
  /** Type of element assiciated with TOC Item */
  elementType: 'h2' | 'h3';
  /** ID of element assiciated with TOC Item */
  id: string;
  /** Text of TOC Item */
  text: string;
};

const SidebarTOC: FC<PropsWithChildren<DropdownTOCProps>> = ({ toc, currentSection }) => (
  <Box
    css={theme => ({
      position: 'sticky',
      top: theme.space.s64,
      display: 'none',
      [theme.breakpoints.bp900.min]: {
        display: 'block',
      },
    })}
  >
    <Heading
      as="h2"
      css={theme => ({ color: theme.colors.coolGrey[80], paddingTop: theme.space.s24 })}
      variant="headingM"
    >
      Content
    </Heading>
    <Box
      css={theme => ({ borderBottom: `solid 1px ${theme.colors.coolGrey[20]}`, margin: `${theme.space.s24}px 0` })}
    />
    <nav>
      <ul css={theme => ({ margin: 0, display: 'flex', flexDirection: 'column', gap: theme.space.s16 })}>
        {toc?.map(item => {
          const isCurrentSection = currentSection && item?.id?.includes(currentSection?.replace('section_', '') || '');

          return item.elementType === 'h2' ? (
            <li key={item.id} css={{ listStyle: 'none' }}>
              <Link
                css={theme => ({
                  color: isCurrentSection ? theme.colors.blue[70] : theme.colors.coolGrey[100],
                  marginBottom: 0,
                  textDecorationColor: theme.colors.coolGrey[100],
                  fontWeight: isCurrentSection ? theme.fontWeights.fw600 : theme.fontWeights.fw400,
                  '&:hover, &:focus, &:active': {
                    color: theme.colors.blue[70],
                  },
                })}
                href={`#${item.id}`}
                id={`link_${item.id}`}
                onClick={evt => scrollIntoView(evt, item.id)}
                onKeyDown={evt => {
                  if (['Enter'].includes(evt.code)) {
                    scrollIntoView(evt, item.id);
                  }
                }}
              >
                {item.text}
              </Link>
            </li>
          ) : (
            <li
              key={item.id}
              css={theme => ({
                display: isCurrentSection ? 'block' : 'none',
                paddingLeft: theme.space.s24,
                listStyle: 'none',
              })}
            >
              <Link
                css={theme => ({
                  color: isCurrentSection ? theme.colors.blue[70] : theme.colors.coolGrey[100],
                  fontWeight: isCurrentSection ? theme.fontWeights.fw600 : theme.fontWeights.fw400,
                  marginBottom: 0,
                  '&:hover, &:focus, &:active': {
                    color: theme.colors.blue[70],
                  },
                })}
                href={`#${item.id}`}
                id={`link_${item.id}`}
                onClick={evt => scrollIntoView(evt, item.id)}
                onKeyDown={evt => {
                  if (['Enter'].includes(evt.code)) {
                    scrollIntoView(evt, item.id);
                  }
                }}
              >
                {item.text}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  </Box>
);

const ColumnTOC: FC<PropsWithChildren<DropdownTOCProps>> = ({ toc }) => {
  // Calculate rows for link grid to render vertically
  const [numColumns, setNumColumns] = useState(4);

  const renderColumnTOC = (numColumns: number) => {
    const numRows = Math.ceil(toc.length / numColumns);
    const chunkedCompetitors = toc
      .map((_, i) => (i % numRows === 0 ? toc.slice(i, i + numRows) : null))
      .filter(Boolean);

    return chunkedCompetitors.map((chunk, i) => (
      <Box key={toc[i]?.id} css={theme => ({ display: 'flex', flexDirection: 'column', gap: theme.space.s16 })}>
        {chunk?.map(item => (
          <li key={item.id} css={{ listStyle: 'none' }}>
            <Link
              css={theme => ({
                paddingLeft: item.elementType === 'h3' ? theme.space.s24 : 0,
                color: theme.colors.coolGrey[100],
                marginBottom: 0,
                '&:hover, &:focus, &:active': {
                  color: theme.colors.blue[70],
                },
              })}
              href={`#${item.id}`}
              id={`link_${item.id}`}
              onClick={evt => scrollIntoView(evt, item.id)}
              onKeyDown={evt => {
                if (['Enter'].includes(evt.code)) {
                  scrollIntoView(evt, item.id);
                }
              }}
            >
              {item.text}
            </Link>
          </li>
        ))}
      </Box>
    ));
  };

  const handleResize = throttle(() => {
    if (isBrowser()) {
      const windowWidth = window.innerWidth;
      setNumColumns(windowWidth < parseInt(BREAKPOINT.BP_500) ? 1 : windowWidth < parseInt(BREAKPOINT.BP_1024) ? 2 : 4);
    }
  });

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  return (
    <>
      <Heading
        as="h2"
        css={theme => ({
          display: 'none',
          color: theme.colors.coolGrey[80],
          paddingTop: theme.space.s24,
          [theme.breakpoints.bp500.min]: {
            display: 'grid',
          },
          [theme.breakpoints.bp900.min]: {
            display: 'none',
          },
        })}
        variant="headingM"
      >
        Content
      </Heading>
      <Box
        css={theme => ({
          gridTemplateColumns: 'repeat(2, 1fr)',
          position: 'relative',
          display: 'none',
          [theme.breakpoints.bp500.min]: {
            display: 'grid',
            gridColumnGap: theme.space.s24,
            gridRowGap: theme.space.s16,
            paddingLeft: 0,
          },
          [theme.breakpoints.bp900.min]: {
            display: 'none',
          },
        })}
      >
        {renderColumnTOC(numColumns)}
      </Box>
      <Box
        css={theme => ({
          borderBottom: `solid 1px ${theme.colors.coolGrey[20]}`,
          display: 'none',
          margin: `${theme.space.s64}px 0`,
          [theme.breakpoints.bp500.min]: {
            display: 'grid',
          },
          [theme.breakpoints.bp900.min]: {
            display: 'none',
          },
        })}
      />
    </>
  );
};

/**
 * generateTOC()
 * Creates the TOC object array
 */
const generateTOC = (htmlStrings: BlogHTMLSection[]) =>
  htmlStrings
    .reduce<TOCItem[]>((previousSections: TOCItem[], currentSection) => {
      const sectionHTML = reactHTMLParser(currentSection.html, {
        replaceOptions: {
          headingItemId: currentSection.sectionID,
          isTOC: true,
        },
      }) as React.JSX.Element | React.JSX.Element[];

      const tocItems = [sectionHTML]
        .flat()
        .reduce<TOCItem[]>((previousItems: TOCItem[], element: React.JSX.Element) => {
          if ((element.type === 'h2' || element.type === 'h3') && typeof element.props.children === 'string')
            return [
              ...previousItems,
              {
                elementType: element.type,
                id: element.props.id,
                text: element.props.children || '',
              },
            ];

          return previousItems;
        }, []);

      return [...previousSections, ...tocItems];
    }, [])
    .filter(item => item.text.trim().length);

/**
 * scrollIntoView()
 * When a Table of Contents item is clicked, scroll to the section in the article
 * */
const scrollIntoView = (evt: SyntheticEvent<HTMLAnchorElement>, id: string) => {
  evt.preventDefault();
  const element = document.querySelector(`#${id}`) as HTMLElement;

  if (element) {
    const topOffset = element.getBoundingClientRect().top + window.scrollY - STICKY_NAV_HEIGHT - 16;

    window.scrollTo({
      top: topOffset,
      behavior: 'smooth',
    });

    // Focus on the element after scrolling
    setTimeout(() => {
      element.focus();
    }, 500);
  }
};

const scrollSelectIntoView = (id: string) => {
  const element = document.querySelector(id);

  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

const BlogTOC: FC<PropsWithChildren<BlogTOCProps>> = ({ content, currentSection }) => {
  const [toc, setToc] = useState([] as TOCItem[]);

  useEffect(() => {
    const blogData = content.reduce<BlogHTMLSection[]>((previous, item) => {
      if (item.collection === 'blog_html') {
        const itemData = item as Lucid.Directus.Generated.Blog_Post_Translations_Content_Blocks;
        const itemContent = itemData.item as Lucid.Directus.Generated.Blog_Html;

        return [
          ...previous,
          {
            html: itemContent?.html || '',
            sectionID: itemContent.id,
          },
        ];
      }

      return previous;
    }, []);

    setToc(generateTOC(blogData));
  }, [content]);

  return (
    <Box
      css={theme => ({
        position: 'relative',
        [theme.breakpoints.bp900.min]: {
          marginTop: theme.space.s24,
          marginRight: theme.space.s24,
        },
      })}
    >
      <Combobox
        css={theme => ({
          [theme.breakpoints.bp500.min]: { display: 'none' },
        })}
        id="table-of-contents"
        onChange={option => scrollSelectIntoView(option.value)}
      >
        <Combobox.Box aria-labelledby="table-of-contents-label">
          <Combobox.Input hasCarot placeholder="Table of Contents" />
          <Combobox.List>
            {toc.map(item => (
              <Combobox.Option key={item.id} text={item.text} value={`#${item.id}`} />
            ))}
          </Combobox.List>
        </Combobox.Box>
      </Combobox>
      <ColumnTOC toc={toc} />
      <SidebarTOC currentSection={currentSection} toc={toc} />
    </Box>
  );
};

export default BlogTOC;
